import { StartlistResponse, ResultResponse, BiathlonEvent } from '../types/api';

// Helper function to try multiple CORS proxies
const fetchWithCorsProxy = async (targetUrl: string): Promise<Response> => {
  // Skip direct fetch, go straight to CORS proxies to avoid console errors
  const proxies = [
    (url: string) => fetch(`https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(url)}`),
    (url: string) => fetch(`https://corsproxy.io/?${encodeURIComponent(url)}`),
    (url: string) => fetch(`https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`),
  ];

  let lastError: Error | null = null;

  for (let i = 0; i < proxies.length; i++) {
    try {
      const response = await proxies[i](targetUrl);
      
      if (response.ok) {
        return response;
      }
      
      lastError = new Error(`HTTP error! status: ${response.status}`);
    } catch (error) {
      lastError = error as Error;
    }
  }

  throw lastError || new Error('All CORS proxy attempts failed');
};

export const fetchStartlistData = async (
  eventId: number
): Promise<StartlistResponse> => {
  const targetUrl = `https://live.eqtiming.com/api/Startlist/${eventId}/0?startAt=1&query=&filter=&sortcols=&count=1000`;

  try {
    console.log('Fetching data from EQ Timing API:', targetUrl);
    const response = await fetchWithCorsProxy(targetUrl);
    const data: StartlistResponse = await response.json();
    console.log('Successfully fetched data from EQ Timing API:', data.Count, 'participants');
    return data;
  } catch (error) {
    console.error('Error fetching startlist data:', error);
    console.log('🚫 All CORS proxies failed. To fix this:');
    console.log('📋 Install a CORS browser extension:');
    console.log('   • Chrome: "CORS Unblock" or "Disable CORS"');
    console.log('   • Firefox: "CORS Everywhere"');
    console.log('   • Edge: "CORS Unblock"');
    console.log('🔄 Using sample data as fallback...');
    return getSampleData();
  }
};

export const fetchResultData = async (
  eventId: number,
  arrangementId: number,
  classId: number
): Promise<ResultResponse> => {
  // Use the correct Result endpoint: /api/Result/Class/{eventId}/{arrangementId}/{classId}
  const targetUrl = `https://live.eqtiming.com/api/Result/Class/${eventId}/${arrangementId}/${classId}`;

  try {
    console.log('Fetching result data from EQ Timing API:', targetUrl);
    const response = await fetchWithCorsProxy(targetUrl);
    const data: ResultResponse = await response.json();
    console.log('Successfully fetched result data:', data.Count, 'participants');
    return data;
  } catch (error) {
    console.error('Error fetching result data:', error);
    // Return empty result if fetch fails
    return {
      Count: 0,
      CompletedCount: 0,
      ItemCount: 0,
      Filter: null,
      CompetitionItems: [],
      Items: {}
    };
  }
};

const getSampleData = (): StartlistResponse => {
  return {
    "Count": 26,
    "Startat": 1,
    "Items": {
      "0": {
        "UID": 13787180,
        "Startnummer": 2,
        "FullStartnummer": 2,
        "Utover": {
          "UID": 6303141,
          "Fornavn": "Esther Sofie Monnest",
          "Etternavn": "Lund",
          "Kjonn": "f",
          "Klubb": {
            "UID": 13940,
            "Navn": "Fossum IF",
            "SAID": 20147
          },
          "Alder": 8,
          "NavnFormatert": "Esther Sofie Monnest Lund"
        },
        "Klasse": {
          "Navn": "J/G 10 Nybeg og yngre",
          "UID": 1171596,
          "Kjonn": "n"
        },
        "Region": {
          "UID": 3533,
          "Navn": "Oslo og Akershus SSK",
          "ShortName": null
        },
        "EtappeDeltaker": [{
          "Starttid": "2025-08-31T10:04:00",
          "StarttidFormatert": "10:04:00"
        }]
      },
      "1": {
        "UID": 13787181,
        "Startnummer": 6,
        "FullStartnummer": 6,
        "Utover": {
          "UID": 6303142,
          "Fornavn": "Sebastian",
          "Etternavn": "Hopsnes",
          "Kjonn": "m",
          "Klubb": {
            "UID": 13940,
            "Navn": "Fossum IF",
            "SAID": 20147
          },
          "Alder": 12,
          "NavnFormatert": "Sebastian Hopsnes"
        },
        "Klasse": {
          "Navn": "J/G 12 Nybeg",
          "UID": 1171597,
          "Kjonn": "m"
        },
        "Region": {
          "UID": 3533,
          "Navn": "Oslo og Akershus SSK",
          "ShortName": null
        },
        "EtappeDeltaker": [{
          "Starttid": "2025-08-31T10:11:00",
          "StarttidFormatert": "10:11:00"
        }]
      },
      "2": {
        "UID": 13787182,
        "Startnummer": 15,
        "FullStartnummer": 15,
        "Utover": {
          "UID": 6303143,
          "Fornavn": "Haakon Munz",
          "Etternavn": "L'Abée-Lund",
          "Kjonn": "m",
          "Klubb": {
            "UID": 13940,
            "Navn": "Fossum IF",
            "SAID": 20147
          },
          "Alder": 14,
          "NavnFormatert": "Haakon Munz L'Abée-Lund"
        },
        "Klasse": {
          "Navn": "J/G 14 Nybeg",
          "UID": 1171598,
          "Kjonn": "m"
        },
        "Region": {
          "UID": 3534,
          "Navn": "Trøndelag SSK",
          "ShortName": null
        },
        "EtappeDeltaker": [{
          "Starttid": "2025-08-31T10:17:00",
          "StarttidFormatert": "10:17:00"
        }]
      },
      "3": {
        "UID": 13787183,
        "Startnummer": 17,
        "FullStartnummer": 17,
        "Utover": {
          "UID": 6303144,
          "Fornavn": "Isak Alf Monnest",
          "Etternavn": "Lund",
          "Kjonn": "m",
          "Klubb": {
            "UID": 13940,
            "Navn": "Fossum IF",
            "SAID": 20147
          },
          "Alder": 10,
          "NavnFormatert": "Isak Alf Monnest Lund"
        },
        "Klasse": {
          "Navn": "J/G 10 Nybeg og yngre",
          "UID": 1171596,
          "Kjonn": "m"
        },
        "Region": {
          "UID": 3533,
          "Navn": "Oslo og Akershus SSK",
          "ShortName": null
        },
        "EtappeDeltaker": [{
          "Starttid": "2025-08-31T10:15:00",
          "StarttidFormatert": "10:15:00"
        }]
      },
      "4": {
        "UID": 13787184,
        "Startnummer": 19,
        "FullStartnummer": 19,
        "Utover": {
          "UID": 6303145,
          "Fornavn": "Kajsa",
          "Etternavn": "Storm",
          "Kjonn": "f",
          "Klubb": {
            "UID": 13940,
            "Navn": "Fossum IF",
            "SAID": 20147
          },
          "Alder": 11,
          "NavnFormatert": "Kajsa Storm"
        },
        "Klasse": {
          "Navn": "J/G 12 Nybeg",
          "UID": 1171597,
          "Kjonn": "f"
        },
        "Region": {
          "UID": 3534,
          "Navn": "Trøndelag SSK",
          "ShortName": null
        },
        "EtappeDeltaker": [{
          "Starttid": "2025-08-31T10:19:00",
          "StarttidFormatert": "10:19:00"
        }]
      }
    }
  };
};

export const fetchBiathlonEvents = async (): Promise<BiathlonEvent[]> => {
  const targetUrl = 'https://events.eqtiming.com/api/Events?query=&dateFrom=&dateTo=&organizationId=0&regionIds=&levelIds=&sportIds=25%2C259%2C382%2C412%2C413&take=1500&dateSort=true&desc=true&onlyValidated=true&onlyshowfororganizer=false&organizerIds=&graded=false&racequality=false';

  try {
    console.log('Fetching biathlon events from EQ Timing API:', targetUrl);
    const response = await fetchWithCorsProxy(targetUrl);
    const data: BiathlonEvent[] = await response.json();
    console.log('Successfully fetched biathlon events:', data.length, 'events');
    return data;
  } catch (error) {
    console.error('Error fetching biathlon events:', error);
    console.log('🚫 All CORS proxies failed. Using sample data as fallback...');
    return getSampleBiathlonEvents();
  }
};

const getSampleBiathlonEvents = (): BiathlonEvent[] => {
  return [
    {
      Id: 78099,
      Name: "Sesongstart Skiskyting 2025",
      Starttime: "2025-11-15T00:00:00",
      EndTime: "2025-11-16T23:59:00",
      Date: "2025-11-15T00:00:00",
      City: {
        Name: "Geilo Skistadion",
        Timezone: "Europe/Oslo",
        Offset: "+0100",
        Country: { Name: "Norway", Iso2: "NO", Iso3: "NOR", Ioc: "NOR", Fifa: "NOR" },
        Coordinate: { Latitude: 60.53650674, Longitude: 8.22587907 }
      },
      Organizer: { Id: 3117, Name: "Norges Skiskytterforbund" },
      Sport: { Id: 25, Name: "Skiskyting", Code: "BT" },
      Dicipline: { Id: 521, Name: "Sprint", Code: "SP" },
      Published: true,
      Validated: true,
      Homepage: "https://www.sesongstartskiskyting.no"
    },
    {
      Id: 78100,
      Name: "Vintercup Skiskyting 2025",
      Starttime: "2025-12-01T00:00:00",
      EndTime: "2025-12-02T23:59:00",
      Date: "2025-12-01T00:00:00",
      City: {
        Name: "Lillehammer",
        Timezone: "Europe/Oslo",
        Offset: "+0100",
        Country: { Name: "Norway", Iso2: "NO", Iso3: "NOR", Ioc: "NOR", Fifa: "NOR" },
        Coordinate: { Latitude: 61.1153, Longitude: 10.4662 }
      },
      Organizer: { Id: 3117, Name: "Norges Skiskytterforbund" },
      Sport: { Id: 25, Name: "Skiskyting", Code: "BT" },
      Dicipline: { Id: 522, Name: "Individual", Code: "IN" },
      Published: true,
      Validated: true,
      Homepage: "https://www.vintercupskiskyting.no"
    }
  ];
};

export const getNext30Events = (events: BiathlonEvent[], lookbackDays: number = 7): BiathlonEvent[] => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Base lookback days from localStorage (permanent)
  let baseLookbackDays = 7;
  try {
    const stored = typeof window !== 'undefined' ? localStorage.getItem('EVENT_LOOKBACK_DAYS') : null;
    if (stored !== null) {
      const parsed = parseInt(stored, 10);
      if (!isNaN(parsed)) {
        baseLookbackDays = Math.min(365, Math.max(0, parsed));
      }
    }
  } catch (e) {
    // Ignore storage access errors
  }

  // Additional lookback days from sessionStorage (session-only)
  let additionalLookbackDays = 0;
  try {
    const stored = typeof window !== 'undefined' ? sessionStorage.getItem('EVENT_ADDITIONAL_LOOKBACK_DAYS') : null;
    if (stored !== null) {
      const parsed = parseInt(stored, 10);
      if (!isNaN(parsed)) {
        additionalLookbackDays = Math.max(0, parsed);
      }
    }
  } catch (e) {
    // Ignore storage access errors
  }

  // Combine both lookback periods and clamp the total
  const totalLookbackDays = Math.min(365, baseLookbackDays + additionalLookbackDays);

  const cutoffDate = new Date(today);
  cutoffDate.setDate(today.getDate() - totalLookbackDays);

  return events
    .filter(event => {
      const eventDate = new Date(event.Date);
      return eventDate >= cutoffDate;
    })
    .sort((a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime())
    .slice(0, 30);
};
